/**
 * The additive R3 fields of GET /api/v2/auth/me, and their safe reading.
 *
 * The OpenAPI document types this route as a bare object, so the shape lives here. Every
 * switch comes from the backend; an absent or malformed value reads as off so a core that
 * predates R3 renders exactly today's UI.
 */

export type MemberScopeMode = "off" | "log" | "enforce";

export interface MeFeatures {
  dashboard_v2: boolean;
  org_invitations: boolean;
  member_grants: boolean;
  org_switch: boolean;
  member_scope: MemberScopeMode;
}

export const FEATURES_OFF: MeFeatures = {
  dashboard_v2: false,
  org_invitations: false,
  member_grants: false,
  org_switch: false,
  member_scope: "off",
};

export type MembershipStatus = "active" | "pending" | "suspended" | "removed" | "none" | (string & {});

export const AGENT_SCOPE_ACTIONS = ["view", "edit", "delete", "execute", "listen", "takeover"] as const;
export type AgentScopeAction = (typeof AGENT_SCOPE_ACTIONS)[number];

/** The caller's resolved agent scope. `null` on /auth/me means unrestricted. */
export interface ResolvedResourceScope {
  source: string;
  grants: Partial<Record<AgentScopeAction, "ALL" | string[]>>;
}

export interface SwitchableOrganization {
  id: string;
  name: string | null;
  role_id: string;
}

/** One category of `organization.entitlements` (core `staff_category_controls`). */
export interface CategoryEntitlement {
  feature_enabled?: boolean;
  owner_control?: boolean;
}

export interface MeOrganization {
  id: string | null;
  name: string | null;
  entitlements: Record<string, CategoryEntitlement>;
}

export interface MeResponse {
  user: Record<string, unknown>;
  account: { id: string; organization_id: string | null; membership_id: string | null };
  organization?: MeOrganization;
  role_id: string | null;
  membership_status?: MembershipStatus;
  resource_scope?: ResolvedResourceScope | null;
  is_system_user?: boolean;
  permissions: string[];
  features?: Partial<MeFeatures>;
  /** Present only while org_switch is on for the current organization. */
  organizations?: SwitchableOrganization[];
}

/** What AuthContext keeps from /auth/me beyond user/account/permissions/role. */
export interface MeSession {
  features: MeFeatures;
  organizations: SwitchableOrganization[];
  membershipStatus: MembershipStatus | null;
  resourceScope: ResolvedResourceScope | null;
  organization: MeOrganization | null;
}

export const EMPTY_ME_SESSION: MeSession = {
  features: FEATURES_OFF,
  organizations: [],
  membershipStatus: null,
  resourceScope: null,
  organization: null,
};

export function readFeatures(raw: unknown): MeFeatures {
  const f = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const scope = f.member_scope;
  return {
    dashboard_v2: f.dashboard_v2 === true,
    org_invitations: f.org_invitations === true,
    member_grants: f.member_grants === true,
    org_switch: f.org_switch === true,
    member_scope: scope === "log" || scope === "enforce" ? scope : "off",
  };
}

export function readMeSession(data: Partial<MeResponse> | null | undefined): MeSession {
  const features = readFeatures(data?.features);
  const organizations = features.org_switch && Array.isArray(data?.organizations)
    ? data!.organizations.filter((o) => o && typeof o.id === "string")
    : [];
  const org = data?.organization;
  return {
    features,
    organizations,
    membershipStatus: typeof data?.membership_status === "string" ? data.membership_status : null,
    resourceScope: data?.resource_scope && typeof data.resource_scope === "object" ? data.resource_scope : null,
    organization: org && typeof org === "object"
      ? {
          id: org.id ?? null,
          name: org.name ?? null,
          entitlements: org.entitlements && typeof org.entitlements === "object" ? org.entitlements : {},
        }
      : null,
  };
}
